import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core';
import { PHX_FILE_TYPE, PHX_IMAGE_MIME_MAPPINGS, PHX_LAYOUT_TYPE, PhxFile, PhxLayoutType, PhxUploadURLProviderFn } from '../file-upload/phx-file-upload.types';
import { PhxImagePanZoomComponent } from '../image-pan-zoom/phx-image-pan-zoom.component';
import { Download, Image, Plus, Rotate, Subtract } from '../icon/icon-map';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PhxTranslatePipe } from '../../translation/phx-translation.pipe';

@Component({
  host: {
    class: 'phx-document-viewer',
  },
  selector: 'phx-document-viewer',
  templateUrl: './phx-document-viewer.component.html',
  styleUrls: ['./phx-document-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhxDocumentViewerComponent),
      multi: true,
    },
  ],
})
export class PhxDocumentViewerComponent implements OnChanges {

  @ViewChild('phxImagePanZoom', { static: false }) public imagePanZoom: PhxImagePanZoomComponent;

  /** Files to be displayed in the document viewer */
  @Input() public phxFiles: PhxFile[] = [];

  /** If true, files will be uploaded automatically upon selection */
  @Input() public phxUploadOnSelect: boolean = false;

  /** Required if phxUploadOnSelect is true */
  @Input() public phxUploadURLProvider?: PhxUploadURLProviderFn;

  /** If true, the component is in edit mode */
  @Input() public phxEditMode: boolean = false;

  /** File max size in MB */
  @Input() public phxFileMaxSize = 5;

  /** Allow multiple selection of files */
  @Input() public phxMultiple: boolean = false;

  /** Placeholder text to show when no image is selected. If not provided, a translated default will be used. */
  @Input() public phxPlaceholderText: string;

  /** Limit number of files to upload */
  @Input() public phxFileUploadLimit?: number;

  /** Whether to obfuscate file information */
  @Input() public phxObfuscate = false;

  /** Emits when file array changes */
  @Output() public phxOnFileChange: EventEmitter<PhxFile[]> = new EventEmitter<PhxFile[]>();

  /** Emits when a file is selected */
  @Output() public phxOnFileSelection: EventEmitter<PhxFile> = new EventEmitter<PhxFile>();

  /** Emits when a file is clicked */
  @Output() public phxOnFileClick: EventEmitter<PhxFile> = new EventEmitter<PhxFile>();

  /** Emits when a file is deleted */
  @Output() public phxOnFileDelete: EventEmitter<PhxFile> = new EventEmitter<PhxFile>();

  /** Emits when a file upload is complete */
  @Output() public phxOnUploadComplete: EventEmitter<PhxFile> = new EventEmitter<PhxFile>();

  /** Emits when PDF pages are extracted */
  @Output() public phxPdfPagesExtracted: EventEmitter<any[]> = new EventEmitter<any[]>();

  /** Emits when a file is updated (such as rotated) */
  @Output() public phxFileUpdated: EventEmitter<any> = new EventEmitter<any>();

  public pdfZoom: number = 1.0;
  public pdfRotation: number = 0;
  public files: PhxFile[] = [];
  public selectedFile: PhxFile | null = null;
  public layout: PhxLayoutType = PHX_LAYOUT_TYPE.INLINE_NO_DETAIL;
  public filesTypesAllowed: PHX_FILE_TYPE = PHX_FILE_TYPE.DOCUMENTS;
  public isImageSelected: boolean = false;
  public isLoading: boolean = false;

  public readonly ZoomInIcon: string = Plus;
  public readonly ZoomOutIcon: string = Subtract;
  public readonly RotateIcon: string = Rotate;
  public readonly DownloadIcon: string = Download;
  public readonly ImageIcon: string = Image;

  private _supportedImageTypes: string[] = Object.values(PHX_IMAGE_MIME_MAPPINGS);
  private _onChange: (files: PhxFile[]) => void;
  private _onTouched: () => void;
  private _initialPinchDistance: number = 0;
  private _isPinching: boolean = false;
  private readonly _pinchThreshold: number = 10;

  constructor(
    private _cdr: ChangeDetectorRef,
    private _phxTranslate: PhxTranslatePipe
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    const { phxFiles } = changes;
    if (phxFiles) {
      if (!this.selectedFile) {
        this.selectedFile = phxFiles.currentValue && phxFiles.currentValue.length > 0 ? phxFiles.currentValue[0] : null;
        this.isImageSelected = this.isImageFile(this.selectedFile);
      }
      this.files = phxFiles.currentValue || [];
    }
  }

  public filesChange(files: PhxFile[]): void {
    this.files = files;
    this.phxOnFileChange.emit(files);
    this._onChange([...files]);
  }

  public onFileSelection(file: PhxFile): void {
    // If new file has error and selected file does not has error then prevent selection
    if ((file?.error || file?.errorMessage) && !(this.selectedFile?.error || this.selectedFile?.errorMessage)) {
      return;
    }

    this.selectedFile = file;
    this.phxOnFileSelection.emit(file);
    this.isImageSelected = this.isImageFile(this.selectedFile);
    this.resetViewerState();

    if (!this.isImageSelected) {
      this.isLoading = true;
    } else {
      this.isLoading = false;
    }

    this._onChange([...this.files, file]);
    this._cdr.markForCheck();
  }

  public onFileClicked({ file }: { file: PhxFile }): void {
    // Prevent selection if:
    // 1. New file has error and selected file does not has error
    // 2. Obfuscate is true
    if ((file?.error || file?.errorMessage) && !(this.selectedFile?.error || this.selectedFile?.errorMessage) || this.phxObfuscate) {
      return;
    }

    if (this.selectedFile && this.isSameFile(file, this.selectedFile)) {
      return;
    }

    this.selectedFile = file;
    this.phxOnFileClick.emit(file);
    this.isImageSelected = this.isImageFile(this.selectedFile);
    this.resetViewerState();

    if (!this.isImageSelected) {
      this.isLoading = true;
    }

    this._cdr.markForCheck();
  }

  public onFileDelete({ file, index }: { file: PhxFile, index: number }): void {
    if (this.isSameFile(this.selectedFile, file)) {
      if (index <= this.files.length - 1) {
        this.selectedFile = this.files[index];
      } else {
        this.selectedFile = this.files[index - 1] || null;
      }
    }
    this.phxOnFileDelete.emit(file);
    this._onChange([...this.files]);
    this.resetViewerState();
    this._cdr.markForCheck();
  }

  public onFileError(file: PhxFile): void {
    if (!this.selectedFile) {
      this.selectedFile = file;
      this.isImageSelected = this.isImageFile(this.selectedFile);
      this._cdr.markForCheck();
    }
  }

  public onUploadComplete(file: PhxFile): void {
    this.phxOnUploadComplete.emit(file);
  }

  public handleRotation(degrees: number): void {
    if (!this.isImageSelected) {
      return;
    }

    const fileIndex: number = this.files.findIndex(f => this.isSameFile(f, this.selectedFile));

    // Preserving the oiringal mime type.
    const originalMimeType = this.selectedFile?.type;
    const rotatedImageData: string = this.imagePanZoom.rotateInDegrees(degrees, originalMimeType);

    const updatedFile: PhxFile = { ...this.selectedFile, url: rotatedImageData };

    this.selectedFile = updatedFile;
    this.files[fileIndex] = updatedFile;
    this.files = [...this.files];

    this.phxFileUpdated.emit({ file: updatedFile, index: fileIndex });
    this._cdr.markForCheck();
  }

  public handleZoomIn(): void {
    if (this.isImageSelected) {
      this.imagePanZoom.zoomIn();
    } else {
      this.pdfZoom = Math.min(2, this.pdfZoom + 0.1);
    }
    this._cdr.markForCheck();
  }

  public handleZoomOut(): void {
    if (this.isImageSelected) {
      this.imagePanZoom.zoomOut();
    } else {
      this.pdfZoom = Math.max(1, this.pdfZoom - 0.1);
    }
    this._cdr.markForCheck();
  }

  /** Handles mouse wheel / trackpad scroll to zoom in or out on the selected file */
  public onMouseWheel(event: WheelEvent): void {
    if (!this.selectedFile || this.selectedFile.error || this.selectedFile.errorMessage) {
      return;
    }

    if (!this.isImageSelected && !event.ctrlKey) {
      return;
    }

    event.preventDefault();

    if (this.isImageSelected) {
      if (!this.imagePanZoom) {
        return;
      }

      // Gradual zoom based on scroll amount for smooth trackpad zooming
      const zoomFactor: number = Math.abs(event.deltaY) / 100;
      const gradualZoom: number = Math.min(zoomFactor * 0.6, 0.6);

      if (event.deltaY < 0) {
        this.imagePanZoom.zoomIn(gradualZoom);
      } else if (event.deltaY > 0) {
        this.imagePanZoom.zoomOut(gradualZoom);
      }
    } else {
      // PDF zoom: adjust in small increments based on scroll direction
      const zoomStep: number = 0.10;
      if (event.deltaY < 0) {
        this.pdfZoom += zoomStep;
      } else if (event.deltaY > 0) {
        this.pdfZoom = Math.max(1, this.pdfZoom - zoomStep);
      }
    }

    this._cdr.markForCheck();
  }

  /** Records the initial pinch distance when two fingers touch the screen */
  public onTouchStart(event: TouchEvent): void {
    if (!this.selectedFile || this.selectedFile.error || this.selectedFile.errorMessage) {
      return;
    }

    if (event.touches.length === 2) {
      this._isPinching = true;
      this._initialPinchDistance = this.getPinchDistance(event.touches[0], event.touches[1]);
      event.preventDefault();
    }
  }

  /** Zooms in or out based on pinch distance change during a two-finger gesture */
  public onTouchMove(event: TouchEvent): void {
    if (!this._isPinching || event.touches.length !== 2) {
      return;
    }

    event.preventDefault();

    const currentPinchDistance: number = this.getPinchDistance(event.touches[0], event.touches[1]);
    const distanceDifference: number = currentPinchDistance - this._initialPinchDistance;

    if (Math.abs(distanceDifference) > this._pinchThreshold) {
      if (distanceDifference > 0) {
        this.handleZoomIn();
      } else {
        this.handleZoomOut();
      }
      // Update initial distance to prevent continuous rapid zooming
      this._initialPinchDistance = currentPinchDistance;
    }
  }

  /** Resets pinch state when fingers are lifted */
  public onTouchEnd(_event: TouchEvent): void {
    if (this._isPinching) {
      this._isPinching = false;
      this._initialPinchDistance = 0;
    }
  }

  private getPinchDistance(touch1: Touch, touch2: Touch): number {
    const dx: number = touch1.clientX - touch2.clientX;
    const dy: number = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  public handleDownload(): void {
    const file = this.selectedFile;
    if (!file || file.error || file.errorMessage) {
      return;
    }
    fetch(file.url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = file.name || this._phxTranslate.transform('file');
        a.click();

        URL.revokeObjectURL(blobUrl);
      })
      .catch(err => {
        console.error('Error downloading file:', err);
      })
  }

  public onContextMenu(event: MouseEvent): void {
    if (this.phxObfuscate) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  public afterLoadComplete(pdf: any): void {
    if (!pdf || !pdf.numPages) {
      return;
    }
    this.isLoading = false;
    this._cdr.markForCheck();

    const pagePromises: Array<Promise<any>> = [];

    for (let i = 1; i <= pdf.numPages; i++) {
      const pagePromise = pdf.getPage(i).catch((error: any) => {
        console.error(`Error loading page ${i}:`, error);
        throw error;
      });

      pagePromises.push(pagePromise);
    }

    Promise.all(pagePromises)
      .then((allPages: any[]) => {
        this.phxPdfPagesExtracted.emit(allPages);
      })
      .catch((error: any) => {
        console.error('Error processing PDF pages:', error);
      });
  }

  public get message(): string {
    if (!this.selectedFile) {
      return this.phxPlaceholderText || this._phxTranslate.transform('Please upload a document');
    }
    if (this.selectedFile.error || this.selectedFile.errorMessage) {
      return this.selectedFile.errorMessage || this._phxTranslate.transform('An error occurred with this file.');
    }
  }

  public writeValue(files: PhxFile[]): void {
    this.phxFiles = files || [];
    const previousSelection = this.selectedFile;
    this.files = files || [];
    const hasPreviousSelection = previousSelection ? files?.some(f => this.isSameFile(f, previousSelection)) : false;
    if (!hasPreviousSelection) {
      this.selectedFile = files && files.length > 0 ? files[0] : null;
      this.isImageSelected = this.isImageFile(this.selectedFile);
      this.resetViewerState();
    }
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (files: PhxFile[]) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  private isSameFile(file1: PhxFile, file2: PhxFile): boolean {
    return file1.url === file2.url;
  }

  private resetViewerState(): void {
    this.pdfZoom = 1.0;
    this.pdfRotation = 0;
  }

  private isImageFile(file: PhxFile | null): boolean {
    return file ? this._supportedImageTypes.includes(file.type || '') : false;
  }

}
